import { useEffect, useState } from "react";
import { ParticipantEvent, type Participant } from "livekit-client";

export type ParticipantRole = "admin" | "user" | "guest" | "twin";

const DEFAULT_ROLE: ParticipantRole = "user";
const VALID_ROLES: ReadonlySet<string> = new Set<ParticipantRole>(["admin", "user", "guest", "twin"]);

function readRole(participant: Pick<Participant, "attributes"> | null | undefined): ParticipantRole {
  const raw = participant?.attributes?.role;
  return raw && VALID_ROLES.has(raw) ? (raw as ParticipantRole) : DEFAULT_ROLE;
}

/** Exported for direct unit testing of the attribute-parsing logic (see Task 12 brief). */
export const __readRoleForTest = readRole;

/**
 * Reads a LiveKit participant's `role` attribute (`admin` | `user` | `guest`,
 * minted server-side into the join token and updated live via the
 * promote/demote endpoint — see #215). Defaults to `"user"` for anything
 * unset or unrecognized.
 *
 * Attributes are pushed to subscribers over the data channel; LiveKit fires
 * `ParticipantEvent.AttributesChanged` on every update rather than
 * re-rendering React itself, so this subscribes and re-reads to stay live —
 * the same reactive-attribute shape `handRaiseMetadata.ts` uses for
 * `metadata`/`ParticipantMetadata`.
 */
export function useParticipantRole(
  participant: Pick<Participant, "attributes" | "on" | "off"> | null | undefined,
): ParticipantRole {
  const [role, setRole] = useState<ParticipantRole>(() => readRole(participant));

  useEffect(() => {
    setRole(readRole(participant));
    if (!participant) return;

    const handleChange = () => setRole(readRole(participant));
    participant.on(ParticipantEvent.AttributesChanged, handleChange);
    return () => {
      participant.off(ParticipantEvent.AttributesChanged, handleChange);
    };
  }, [participant]);

  return role;
}
